import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { accessLocalStorage, createOutboundGroup, getOutboundGroupTrunks } from '../../../services';

import '../../../libs/bulma.min.css';
import './OutboundCallsForm.css';

import Loading from '../../../components/Loading/LoadingModule';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faTimes, faUsers, faInfoCircle } from '@fortawesome/free-solid-svg-icons';

const SLUG_RE = /^[a-z0-9]{2,8}$/;

function OutboundGroupsCreate() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [trunks, setTrunks] = useState([]);
  const [tenant, setTenant] = useState('');
  const [errors, setErrors] = useState({});
  const [alert, setAlert] = useState({ show: false, type: '', message: '' });

  const [values, setValues] = useState({
    slug: '',
    name: '',
    description: '',
    trunkName: '',
    ispCode: '',
    digitRemove: ''
  });

  const history = useHistory();

  const slugBudget = Math.max(0, Math.min(8, 25 - String(tenant || '').length));

  const loadData = useCallback(async () => {
    const dataUser = accessLocalStorage.getUserLocalStorage();
    if (!dataUser) return history.push('/');

    const currentTenant = dataUser.user ? dataUser.user.idtenant : '';
    setTenant(currentTenant || '');

    const response = await getOutboundGroupTrunks(currentTenant);
    setTrunks(response && response.success && response.data ? response.data : []);
    setLoading(false);
  }, [history]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleInputChange = (field, value) => {
    setValues((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const validate = () => {
    const found = {};

    if (!SLUG_RE.test(values.slug)) {
      found.slug = 'Use de 2 a 8 caracteres, apenas letras minúsculas e números';
    } else if (values.slug.length > slugBudget) {
      found.slug = `Máximo de ${slugBudget} caracteres para este cliente`;
    } else if (values.slug === 'web') {
      found.slug = 'Identificador reservado';
    }

    if (!values.name.trim()) found.name = 'Nome é obrigatório';
    if (!values.trunkName) found.trunkName = 'Selecione o tronco de saída do grupo';
    if (values.ispCode && !/^[0-9]{1,10}$/.test(values.ispCode)) {
      found.ispCode = 'Somente dígitos (até 10)';
    }
    if (values.digitRemove !== '' && !(Number.isInteger(Number(values.digitRemove)) && Number(values.digitRemove) >= 0 && Number(values.digitRemove) <= 10)) {
      found.digitRemove = 'Informe um número entre 0 e 10';
    }

    setErrors(found);
    return Object.keys(found).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;

    setSaving(true);
    const payload = {
      tenant,
      slug: values.slug.trim().toLowerCase(),
      name: values.name.trim(),
      description: values.description.trim() || null,
      trunkName: values.trunkName
    };
    if (values.ispCode !== '') payload.ispCode = values.ispCode;
    if (values.digitRemove !== '') payload.digitRemove = Number(values.digitRemove);

    const response = await createOutboundGroup(payload);
    setSaving(false);

    if (response && response.success) {
      setAlert({ show: true, type: 'success', message: 'Grupo criado com sucesso. Atribua a faixa de ramais na tela de edição.' });
      setTimeout(() => history.push('/config/chamadas/saida/grupos'), 1800);
      return;
    }

    setAlert({
      show: true,
      type: 'error',
      message: (response && response.error) || 'Não foi possível criar o grupo'
    });
  };

  const handleCancel = () => history.push('/config/chamadas/saida/grupos');

  const selectedTrunk = trunks.find((trunk) => trunk.trunkName === values.trunkName);

  if (loading) return <Loading />;

  return (
    <div className="outbound-form-container">
      <div className="outbound-form-header">
        <div className="outbound-form-title-wrapper">
          <FontAwesomeIcon icon={faUsers} className="outbound-form-icon" />
          <div>
            <h1 className="outbound-form-title">Novo Grupo de Saída</h1>
            <p className="outbound-form-subtitle">Direcionar uma faixa de ramais para um tronco específico</p>
          </div>
        </div>
        <button className="btn-back" onClick={handleCancel}>
          <FontAwesomeIcon icon={faTimes} />
          Voltar
        </button>
      </div>

      {alert.show && (
        <div className="alerts-section">
          <div className={`alert-message alert-${alert.type}`}>
            <div className="alert-header">
              <span>{alert.type === 'success' ? 'Sucesso!' : 'Erro!'}</span>
              <button className="alert-close" onClick={() => setAlert({ ...alert, show: false })}>×</button>
            </div>
            <div className="alert-body">{alert.message}</div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="form-section-card">
          <div className="form-grid">
            <div className="form-field">
              <label className="form-label">Cliente</label>
              <input className="form-input" type="text" value={tenant} readOnly disabled />
            </div>

            <div className="form-field">
              <label className="form-label">Nome do Grupo *</label>
              <input
                className={`form-input ${errors.name ? 'is-danger' : ''}`}
                type="text"
                value={values.name}
                maxLength={100}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="Ex.: Comercial"
              />
              {errors.name && <p className="form-help is-danger">{errors.name}</p>}
            </div>

            <div className="form-field">
              <label className="form-label">Identificador *</label>
              <input
                className={`form-input ${errors.slug ? 'is-danger' : ''}`}
                type="text"
                value={values.slug}
                maxLength={slugBudget}
                onChange={(e) => handleInputChange('slug', e.target.value.toLowerCase())}
                placeholder="Ex.: com"
              />
              {errors.slug && <p className="form-help is-danger">{errors.slug}</p>}
              <p className="form-help">
                Até {slugBudget} caracteres (letras minúsculas e números). Não pode ser alterado depois de criado.
              </p>
            </div>

            <div className="form-field">
              <label className="form-label">Tronco de Saída *</label>
              <select
                className={`form-select ${errors.trunkName ? 'is-danger' : ''}`}
                value={values.trunkName}
                onChange={(e) => handleInputChange('trunkName', e.target.value)}
              >
                <option value="">Selecione o tronco</option>
                {trunks.map((trunk) => (
                  <option key={trunk.trunkName} value={trunk.trunkName}>
                    {trunk.trunkName}{trunk.fromUser ? ` — ${trunk.fromUser}` : ''}
                  </option>
                ))}
              </select>
              {errors.trunkName && <p className="form-help is-danger">{errors.trunkName}</p>}
              {selectedTrunk && !selectedTrunk.fromUser && (
                <p className="form-help is-warning">
                  Tronco sem número de origem configurado; o número apresentado dependerá da operadora.
                </p>
              )}
              {selectedTrunk && selectedTrunk.fromUser && (
                <p className="form-help is-info">
                  <FontAwesomeIcon icon={faInfoCircle} /> Ramais deste grupo apresentarão o número {selectedTrunk.fromUser}.
                </p>
              )}
            </div>

            <div className="form-field">
              <label className="form-label">Código Operadora</label>
              <input
                className={`form-input ${errors.ispCode ? 'is-danger' : ''}`}
                type="text"
                value={values.ispCode}
                onChange={(e) => handleInputChange('ispCode', e.target.value)}
                placeholder="Herdado das rotas padrão"
              />
              {errors.ispCode && <p className="form-help is-danger">{errors.ispCode}</p>}
              <p className="form-help">Deixe vazio para herdar o código de cada rota de saída do cliente.</p>
            </div>

            <div className="form-field">
              <label className="form-label">Remove Dígitos</label>
              <input
                className={`form-input ${errors.digitRemove ? 'is-danger' : ''}`}
                type="number"
                min="0"
                max="10"
                value={values.digitRemove}
                onChange={(e) => handleInputChange('digitRemove', e.target.value)}
                placeholder="Herdado das rotas padrão"
              />
              {errors.digitRemove && <p className="form-help is-danger">{errors.digitRemove}</p>}
            </div>

            <div className="form-field form-field-full">
              <label className="form-label">Descrição</label>
              <textarea
                className="form-textarea"
                rows="3"
                value={values.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Descrição opcional do grupo"
              />
            </div>

            <div className="form-field form-field-full">
              <p className="form-help is-info">
                <FontAwesomeIcon icon={faInfoCircle} /> As máscaras de discagem do grupo são herdadas das rotas ativas em
                Chamadas de Saída, trocando apenas o tronco. Se o cliente não tiver rota para um tipo de chamada, os
                ramais do grupo também não conseguirão fazer aquele tipo de chamada.
              </p>
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="btn-cancel" onClick={handleCancel} disabled={saving}>
              Cancelar
            </button>
            <button type="submit" className={`btn-submit ${saving ? 'is-loading' : ''}`} disabled={saving}>
              <FontAwesomeIcon icon={faSave} />
              Criar Grupo
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

export default OutboundGroupsCreate;
